import { useImperativeHandle, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Info, BedDouble, Bath, Ruler } from "lucide-react";
import useSwipePhysics from "@/lib/use_swipe_physics";
import { priceParts } from "@/lib/pricing";
import { collectAmenities, labelFor } from "@/lib/amenities";
import BadgeVerified from "@/components/badges/badge_verified";
import { ListerBadge } from "@/components/badges/badge_realtor";
import BadgeBoost from "@/components/badges/badge_boost";
import ListerAvatar from "@/components/swipe/lister_avatar";

// The landing hero card: one giant landscape photo on desktop, 3:4 on mobile.
// Swipe physics come from the shared Tinder-grade hook: 1:1 pointer tracking,
// grab-point rotation pivot, velocity + distance release, linear exit. Buttons
// and arrow keys go through `ref.swipe(action)` so they run the same fly-out.
// Tapping the photo opens the profile expand popup — it never navigates away.
export default function HeroCard({ listing, onSwipe, onExpand, active = true, mirrorX, onExited, enterFrom = 0, hint = false, ref }) {
  const [photo, setPhoto] = useState(0);
  const pressRef = useRef(null);
  const { likeOpacity, nopeOpacity, triggerSwipe, runPreview, style, dragProps } = useSwipePhysics({
    onSwipe: (action) => onSwipe(action, listing),
    onExited,
    enabled: active,
    mirrorX,
    enterFrom,
  });

  useImperativeHandle(ref, () => ({ swipe: triggerSwipe, preview: runPreview }), [triggerSwipe, runPreview]);

  const photos = listing.photos || [];

  // A real tap (not a drag) on the photo opens the profile expand popup.
  // Browsing is free, so this never goes through the signup gate.
  function handlePointerDown(e) {
    pressRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  }

  function handlePhotoClick(e) {
    const press = pressRef.current;
    pressRef.current = null;
    if (!press) return;
    const moved = Math.hypot(e.clientX - press.x, e.clientY - press.y);
    if (moved < 8 && Date.now() - press.t < 400) onExpand?.(photo);
  }

  return (
    <motion.div
      className={`absolute inset-0 touch-none select-none overflow-hidden rounded-3xl bg-neutral-900 shadow-ambient ${
        active ? "cursor-grab active:cursor-grabbing" : ""
      }`}
      style={style}
      {...dragProps}
    >
      {/* Photo full bleed — landscape crops center themselves. Tap opens the profile. */}
      <div className="absolute inset-0" onPointerDown={handlePointerDown} onClick={handlePhotoClick}>
        <img src={photos[photo]} alt={listing.title} draggable={false} loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
        {/* Legibility band only — the single allowed gradient */}
        <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/90 via-black/55 to-transparent" />
      </div>

      {/* Photo progress dots — top center. Dots cycle photos, never open the profile. */}
      <div className="absolute inset-x-0 top-4 z-10 flex justify-center gap-1.5">
        {photos.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Show photo ${i + 1}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setPhoto(i); }}
            className={`h-1 rounded-full transition-all ${i === photo ? "w-7 bg-white lg:w-9" : "w-3 bg-white/40 lg:w-4"}`}
          />
        ))}
      </div>

      {/* Details button stays top right; the lister avatar + badges now live
          down in the bottom info band, above the price, matching the Swipe
          page. */}
      {/* First-visit "signal" ripple: concentric rings pulse out of the info
          button like it's broadcasting — a hint to tap it. Only on the guest
          intro (gated by `hint`), on the live top card. */}
      {hint && active && (
        <span aria-hidden="true" className="pointer-events-none absolute right-4 top-4 h-10 w-10 lg:right-5 lg:top-5">
          <span className="absolute inset-0 rounded-full bg-white/50 animate-[sr-signal_2.8s_ease-out_infinite]" />
          <span className="absolute inset-0 rounded-full bg-white/40 animate-[sr-signal_2.8s_ease-out_infinite] [animation-delay:1.4s]" />
        </span>
      )}
      <button
        type="button"
        aria-label="View details"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onExpand?.(photo); }}
        className={`absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-white transition lg:right-5 lg:top-5 ${
          hint && active ? "bg-white/40 ring-2 ring-white/70" : "bg-white/25 hover:bg-white/40"
        }`}
      >
        <Info size={19} />
      </button>

      {/* Giant drag stamps */}
      <motion.span
        style={{ opacity: likeOpacity }}
        className="absolute left-6 top-16 -rotate-[18deg] rounded-xl border-4 border-brand-primary bg-white/80 px-5 py-1.5 text-4xl font-extrabold tracking-[5px] text-brand-primary lg:left-10 lg:top-20 lg:text-6xl"
      >
        LIKE
      </motion.span>
      <motion.span
        style={{ opacity: nopeOpacity }}
        className="absolute right-6 top-16 rotate-[18deg] rounded-xl border-4 border-brand-ink bg-white/80 px-5 py-1.5 text-4xl font-extrabold tracking-[5px] text-brand-ink lg:right-10 lg:top-20 lg:text-6xl"
      >
        NOPE
      </motion.span>

      {/* Bottom info band. Extra bottom padding on mobile lifts the
          price/title/beds clear of the action buttons that now STRADDLE the
          card's bottom edge (their upper halves cover the lowest ~28px), so the
          text is never occluded. Desktop keeps its own padding. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 p-5 pb-16 text-white lg:px-9 lg:pb-9">
        {/* Lister avatar + badges sit just above the price, like the Swipe
            page. Tap the avatar for the lister's name + link to their listings
            (popover opens upward since it sits low on the card). */}
        <div className="pointer-events-auto mb-2 flex flex-wrap items-center gap-2">
          <ListerAvatar listing={listing} above />
          {listing.verified && <BadgeVerified />}
          <ListerBadge type={listing.lister_type} />
          {listing.boosted && <BadgeBoost />}
        </div>
        <p className="font-display text-4xl font-semibold tracking-tight lg:text-[44px] lg:leading-none">
          ${priceParts(listing).amount.toLocaleString()}
          <span className="font-sans text-base font-normal text-white/80"> {priceParts(listing).unit}</span>
        </p>
        <h3 className="mt-1 text-lg font-medium leading-snug lg:mt-2 lg:text-2xl">{listing.title}</h3>
        {/* WHY the join: "City, PROVINCE" is the allowed geographic comma
            label ("Coquitlam, BC"), and filter(Boolean) keeps a listing with
            a missing neighborhood from rendering a dangling comma. */}
        <p className="mt-0.5 text-sm text-white/80 lg:text-base">
          {[listing.neighborhood, listing.city, listing.province].filter(Boolean).join(", ")}
          {listing.distance_km != null && <> · {listing.distance_km} km</>}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 lg:mt-4">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/25 px-3 py-1 text-xs font-semibold text-white lg:text-[13px]">
            <BedDouble size={13} /> {listing.beds === 0 ? "Studio" : `${listing.beds} bd`}{listing.dens ? ` + ${listing.dens} den` : ""}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/25 px-3 py-1 text-xs font-semibold text-white lg:text-[13px]">
            <Bath size={13} /> {listing.baths} ba
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/25 px-3 py-1 text-xs font-semibold text-white lg:text-[13px]">
            <Ruler size={13} /> {listing.sqft} sqft
          </span>
          {collectAmenities(listing).slice(0, 3).map((a) => (
            <span key={a} className="hidden rounded-full bg-white/25 px-3 py-1 text-[13px] font-semibold text-white lg:inline-flex">
              {labelFor(a)}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
